import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { ErrorMessage, InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { CurrencyInput } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';
import { Label } from '@eika/label';

import {
    FormTypeCalculatorVehicleInfo,
    fetchEuroTaxLoanCommitment,
    fetchOngoingApplicationEuroTax,
    fetchOngoingApplicationEuroTaxCleared,
} from '../../actions';
import Loader from '../../components/Loader/Loader';
import { UpgradeToVehicleLoanRequest } from '../../generated-types';
import { AppState } from '../../reducers';
import { CustomerCaseInfoLandingPageUpdate } from '../../reducers/ongoingapplications';
import { errorNames } from '../../util/mappings/errorName';
import LoanProduct from '../../util/mappings/loanProduct';
import LoanProductRegValidations, { LoanProductRegValid } from '../../util/mappings/loanProductRegValidations';
import errorText from '../../validation/validationTexts';

interface Props {
    application: CustomerCaseInfoLandingPageUpdate;
}

interface LoanUpdateFormValues {
    regNr: string;
    vehiclePrice: number | null;
    odometerValue: number | null;
}

const LoanUpdateForm = ({ application }: Props): JSX.Element => {
    const dispatch = useDispatch<any>();
    const { vehicle, context, ongoingApplications, loader, calculator, completion } = useSelector(
        (state: AppState) => state,
    );
    const { data } = context.config;
    const { text } = data;
    const { applicationId, loanProduct } = application;
    const { selectedError } = ongoingApplications;
    const eurotaxPlateNotFound = selectedError && selectedError === errorNames.eurotaxPlateNotFound;
    const { loanAmountWithoutFees, odometerValue: storedOdometerValue } = calculator.loanInfo;
    const applicationDetailsLoaded =
        completion.ongoing.applicationId === applicationId && loanAmountWithoutFees !== undefined;

    const { manufacturer, registrationNumber, manufactureYear, importedUsed, model, co2, fuel } =
        vehicle.vehicleDetails;
    const regNrValidateObject: LoanProductRegValid = LoanProductRegValidations(loanProduct);
    const vehicleRegistrationNumberLength = regNrValidateObject.max;

    const MAX_DEFAULT_DURATION = 10;
    const DEFAULT_DURATION = 14;
    const NO_INSURACE_DURATION = 5;
    const ELECTRIC_FUEL = 'ELEKTRISITET';
    const HYDROGEN_FUEL = 'HYDROGEN';
    const isCaravan = loanProduct === LoanProduct.campingCaravan;

    let vehicleTooOld = false;
    let vehicleHasEmmision = false;

    const vehicleInfo = `${manufacturer} ${model && model !== text.eurotaxUnknown ? model : ''} ${manufactureYear}${
        importedUsed ? ` ${text.eurotaxImported}` : ''
    }`;

    let maxAge: number | null = null;
    const currentYear = new Date().getFullYear();

    if (manufactureYear) {
        if (currentYear - manufactureYear <= 4) {
            maxAge = MAX_DEFAULT_DURATION;
        } else {
            maxAge = DEFAULT_DURATION - (currentYear - manufactureYear);
        }
    } else {
        maxAge = MAX_DEFAULT_DURATION;
    }

    maxAge = loanProduct === LoanProduct.carNoInsurance ? NO_INSURACE_DURATION : maxAge;

    if (loanProduct === LoanProduct.carElectric) {
        if (fuel !== ELECTRIC_FUEL && fuel !== HYDROGEN_FUEL) {
            vehicleHasEmmision = true;
        } else {
            vehicleHasEmmision = !(co2 === '' || co2 === '0000' || Number(co2) === 0);
        }
    }

    vehicleTooOld = !!(
        loanProduct !== LoanProduct.carNoInsurance &&
        manufactureYear &&
        currentYear - manufactureYear > DEFAULT_DURATION
    );
    const shortMode = vehicleTooOld || vehicleHasEmmision;

    const loanUpdateSchema: yup.ObjectSchema<LoanUpdateFormValues> = yup.object({
        regNr: yup
            .string()
            .trim()
            .min(regNrValidateObject.min, errorText.regNoRequired)
            .max(regNrValidateObject.max, errorText.regNoRequired)
            .matches(regNrValidateObject.expression, errorText.regNoNotValid)
            .required(errorText.regNoRequired),
        vehiclePrice: shortMode
            ? yup.number().nullable().default(null)
            : yup
                  .number()
                  .typeError(errorText.vehiclePriceRequired)
                  .integer(errorText.vehiclePriceRequired)
                  .positive(errorText.vehiclePricePositive)
                  .required(errorText.vehiclePriceRequired),
        odometerValue:
            shortMode || isCaravan
                ? yup.number().nullable().default(null)
                : yup
                      .number()
                      .typeError(errorText.odometerValueRequired)
                      .integer(errorText.odometerValueRequired)
                      .positive(errorText.odometerValuePositive)
                      .required(errorText.odometerValueRequired),
    });

    const {
        handleSubmit,
        register,
        setValue,
        formState: { errors },
    } = useForm<LoanUpdateFormValues>({
        resolver: yupResolver(loanUpdateSchema) as Resolver<LoanUpdateFormValues>,
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {
            vehiclePrice: null,
            odometerValue: storedOdometerValue ?? null,
            regNr: registrationNumber || '',
        },
    });

    React.useEffect(() => {
        if (applicationDetailsLoaded && storedOdometerValue !== undefined && storedOdometerValue !== null) {
            setValue('odometerValue', storedOdometerValue);
        }
    }, [applicationDetailsLoaded, setValue, storedOdometerValue]);

    const mergeData = (form: LoanUpdateFormValues) => {
        const payload = { ...form } as UpgradeToVehicleLoanRequest;
        payload.preViewCaseId = applicationId;
        payload.imported = !!importedUsed;
        payload.manufacturer = manufacturer || '';
        payload.model = model && model !== text.eurotaxUnknown ? model : null;
        payload.loanProduct = loanProduct;
        payload.odometerValue = isCaravan ? 0 : Number(form.odometerValue || 0);
        payload.modelYear = manufactureYear || currentYear;
        payload.loanPeriodInMonths = (maxAge || MAX_DEFAULT_DURATION) * 12;
        payload.equity = Math.max(0, Number(form.vehiclePrice || 0) - Number(loanAmountWithoutFees));
        return payload;
    };

    const disableRegNumber = () => {
        if (vehicleHasEmmision) {
            return false;
        }

        return !!registrationNumber && !vehicleTooOld;
    };

    if (
        loader.isFetchingVehicleInfo ||
        loader.isFetchingApplication ||
        loader.isUpgradingOngoingApplication ||
        (!applicationDetailsLoaded && !loader.fetchApplicationFailed)
    ) {
        return (
            <div className="vehicle-info eurotax-search-form">
                <Loader title={text.loadingDefault} />
            </div>
        );
    }

    return (
        <>
            <div className="vehicle-info eurotax-full-form">
                <form
                    onSubmit={handleSubmit((form: LoanUpdateFormValues) => {
                        if (vehicleTooOld || vehicleHasEmmision) {
                            dispatch(fetchOngoingApplicationEuroTaxCleared(applicationId));
                            dispatch(
                                fetchOngoingApplicationEuroTax(
                                    {
                                        vehicleRegistrationNumber: form.regNr,
                                    } as FormTypeCalculatorVehicleInfo,
                                    applicationId,
                                ),
                            );
                        } else {
                            dispatch(fetchEuroTaxLoanCommitment(applicationId, mergeData(form)));
                        }
                    })}
                >
                    <InfoMessage
                        className="loan-info__info-message"
                        icon={IconTypes.LYSPAERE_24}
                        text={text.eurotaxSearchErrorText1}
                    />

                    {vehicleInfo && (
                        <div className="vehicle-info__result">
                            <Label text={vehicleInfo} />
                        </div>
                    )}
                    <SearchInput
                        {...register('regNr')}
                        label={text.eurotaxRegNumber}
                        type="text"
                        minLength={vehicleRegistrationNumberLength}
                        error={errors?.regNr?.message || ''}
                        disabled={disableRegNumber()}
                        onChange={(e: any) => {
                            setValue(
                                e.target.name,
                                e.target.value
                                    .replace(/[^\d\w]/g, '')
                                    .substring(0, vehicleRegistrationNumberLength)
                                    .toUpperCase(),
                            );
                        }}
                    />
                    {vehicleHasEmmision && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.eurotaxEmissionWarning}
                        />
                    )}
                    {!vehicleTooOld && !vehicleHasEmmision && (
                        <>
                            <CurrencyInput
                                {...register('vehiclePrice')}
                                label={text.eurotaxPrice}
                                placeholder={text.eurotaxEquityPlaceholder}
                                error={errors?.vehiclePrice?.message || ''}
                                prefix=""
                            />

                            {!isCaravan && (
                                <CurrencyInput
                                    {...register('odometerValue')}
                                    label={text.calculatorVehicleInfoLabelOdometerValue}
                                    placeholder={text.eurotaxOdometerPlaceholder}
                                    error={errors?.odometerValue?.message || ''}
                                    max={100000000000}
                                    prefix=""
                                    suffix=" km"
                                />
                            )}
                        </>
                    )}

                    <div className="vehicle-submit__button">
                        <StandardButton type="submit">
                            {!vehicleTooOld ? text.eurotaxSubmitButton : text.eurotaxSearchButton}
                        </StandardButton>
                    </div>
                    {vehicleTooOld && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.calculatorVehicleInfoErrorObjectAge}
                        />
                    )}
                    {eurotaxPlateNotFound && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.eurotaxPlateNotFoundWarning}
                        />
                    )}
                </form>
            </div>
        </>
    );
};
export default LoanUpdateForm;
